/* The sea-current field's store: a WINDOW of model hours, held and sampled.
 *
 * The rules and the maths are in ./seacurrents (plain `.ts`, so `mock:verify`
 * can import them under bare node). This file is state, a fetch and a TTL, and
 * nothing that cannot be seen from the outside.
 *
 * ═══ WHY IT HOLDS A WINDOW NOW, AND NOT TWO HOURS ═══
 *
 * It used to hold exactly the two hours a blend needs, because an hour was
 * 3.5 MB of JSON and more than two was megabytes of iPad for nothing. That made
 * the scrubber jerky in a way no amount of render tuning could fix: a drag
 * across its 60 h span crosses about sixty hour boundaries, each one a fresh
 * multi-megabyte fetch started from a 1 Hz tick — and dragging BACK re-fetched
 * what had just been evicted.
 *
 * The pi now serves the whole span as one binary blob at ~222 kB an hour (wet
 * cells only, centimetres per second, Int16 — see ./seacurrents), so 42 hours
 * is about 9.3 MB. That is small enough to simply keep, which means a scrub
 * costs NO NETWORK AT ALL once the window has landed. The eviction is gone with
 * the problem it existed for.
 *
 * ═══ OFFLINE IS A REAL ANSWER, NOT AN ERROR ═══
 *
 * There is no shipped baseline for this layer — deliberately, for now: the
 * investigation's phase 1 would add one, and until then the honest behaviour
 * at anchor with the radio off is for the row to grey out and SAY SO, rather
 * than to draw a field from whenever the pi was last reachable. The tides and
 * the tidal streams still work offline; they are shipped data and this is not.
 */

import {
  bracket,
  decodeWindow,
  gridFor,
  modelHour,
  packLattice,
  provenance,
  windowCurrentSampler,
  windowSampler,
  type CurrentGeom,
  type CurrentGrid,
  type PackedHour,
  type SeaCurrentLattice,
  type SeaCurrentResponse
} from '@gennetay/chart-core/lib/seacurrents';
import type { FieldSampler } from '@gennetay/chart-core/lib/windparticles';
import { sources } from '@gennetay/chart-core/sources';
import type { Bbox } from '@gennetay/chart-core/lib/geo';

/* ═══ TWO PROVIDERS, ONE FIELD ═══
 *
 * The same NEMO window is served two ways, and the BYTES ARE IDENTICAL — the
 * Worker feed writes the same 'SCW1' format from a port of the plugin's own
 * pack, so `decodeWindow` cannot tell them apart and nothing below this line
 * needs to.
 *
 *   marine-data/seacurrents/window          the pi's plugin, on the boat
 *   feeds/feed/salish.seacurrents           the layer cache, hosted
 *
 * The pi WINS where both exist: same network as the tablet, holds the window
 * across a reboot, and answers with no internet at all — which is the case the
 * whole cache exists for. */
const ROUTE = 'seacurrents';
const WINDOW_ROUTE = `${ROUTE}/window`;
const FEED_WINDOW = 'feed/salish.seacurrents';

const provider = (): 'marine-data' | 'feeds' | null => {
  const s = sources();
  if (s.has('marine-data')) return 'marine-data';
  if (s.has('feeds')) return 'feeds';
  return null;
};
/** Mirrors the pi's own hour cache. Asking faster cannot produce a new field. */
const TTL_MS = 30 * 60_000;
/** After a failure, how long before trying again — and the ceiling. A boat out
 *  of signal must not hammer a route that cannot answer. */
const RETRY_MIN_MS = 20_000;
const RETRY_MAX_MS = 10 * 60_000;
const TIMEOUT_MS = 90_000;

const HOUR = 3_600_000;

/** How far either side of the cursor the store tries to hold. Matches the
 *  scrubber's own span (SCRUB_BACK_MS / SCRUB_FWD_MS) with an hour of slack at
 *  each end, so the ends of the slider are inside the window rather than on its
 *  edge. Not imported from viewtime — this store is loaded by a map module and
 *  must not drag the cursor onto that chunk. */
const WANT_BACK_H = 13;
const WANT_FWD_H = 49;

/** Hours per request. The whole span in one go is a single 9 MB response that
 *  arrives all-or-nothing after several seconds; in chunks, the hours nearest
 *  the cursor land first and the layer is usable while the rest fills in
 *  behind. Eight is about 1.8 MB — a second or so on the boat's link. */
const CHUNK_H = 8;

/** How many chunk requests may be in the air at once. The pi fetches each from
 *  ERDDAP, and three concurrent netCDF extracts is where a Pi 4 stops getting
 *  faster and starts queueing. */
const CONCURRENCY = 2;

/**
 * The most field this may hold, in bytes.
 *
 * ═══ A BUDGET, NOT AN ASSUMPTION ═══
 *
 * On the boat's own lattice an hour is 55,484 wet cells — 222 kB — so the whole
 * 62-hour span is about 15 MB, which is the trade this design makes and is fine
 * on an iPad. But that number comes from the coastline, and the coastline is
 * not something this file controls: the mock's synthetic channel is 2.5× wetter
 * and holds 36 MB for the same span, and a future lattice at a finer step would
 * be worse again.
 *
 * "It will be about fifteen megabytes" is an assumption; this makes it a
 * guarantee. Past the budget the window shrinks FORWARD first — the hours just
 * ahead of now are the ones a passage plan is made from, and the far end of the
 * scrubber is where the model runs out anyway.
 */
const BUDGET_BYTES = 24 * 1024 * 1024;
/** Never go below this, whatever the arithmetic says: a window smaller than a
 *  tide is not a window, and the layer would be back to fetching on a drag. */
const MIN_FWD_H = 12;

/** How long a chunk that came back with no hours is left alone. Long enough
 *  that it cannot become a request loop, short enough that a new model run
 *  extending the forecast is picked up within the hour. */
const EMPTY_TTL_MS = 20 * 60_000;

class SeaCurrents {
  /** Bumped whenever the held data changes, so a consumer can wake a loop. */
  rev = $state(0);
  error = $state<string | null>(null);
  source = $state('SalishSeaCast NEMO (UBC)');
  /** When the pi last gave us a fresh field. */
  at = $state<number | null>(null);
  /** The pi answered from its own stale cache. */
  stale = $state(false);
  /** How many model hours are held, and their span — for the scrubber's
   *  coverage shading and for the row's own wording. Plain `$state` numbers
   *  rather than a derived over the map, so reading them in a template does not
   *  pull ten megabytes of Int16Array into a reactive dependency. */
  held = $state(0);
  from = $state<number | null>(null);
  to = $state<number | null>(null);

  /**
   * Where the MODEL reaches, as the pi reports it — not what we hold.
   *
   * ═══ THE FIX FOR "THE CURRENT MAP VANISHES" ═══
   *
   * SalishSeaCast publishes a rolling window, measured at 135 h back and only
   * ~30 h forward, while the scrubber reaches +48 h. This store used to ask for
   * its full forward span regardless; ERDDAP 404s a range whose end is past the
   * model, the pi reported 502, `#failed()` set `error`, `available()` went
   * false, and `layers.svelte.ts` REMOVED THE LAYER FROM THE MAP. Then the
   * backoff expired, it tried the same impossible hours, and failed again — so
   * the layer flickered in and out, which is the "sometimes takes a long time
   * to activate, and seems buggy".
   *
   * Knowing the reach means those hours are never requested. The visual side —
   * the coverage bar under the scrubber, the fade past the edge — was already
   * built for exactly this and was being pre-empted by the layer being torn off
   * the map before it could show anything.
   */
  coverage = $state<{ from: number; to: number } | null>(null);

  /** The hours themselves, deliberately NOT `$state`: they are megabytes of
   *  typed array, they are only ever read by the render loop, and `rev` is the
   *  reactive signal that says they changed. */
  #hours = new Map<number, PackedHour>();
  #geom: CurrentGeom | null = null;

  /**
   * The model's own footprint, or null before a field has landed.
   *
   * SalishSeaCast is a REGIONAL run — it stops at the mouth of Juan de Fuca and
   * has nothing to say about Puget Sound, let alone Oregon. The lattice header
   * carries its own corners, so the layer's `coverage` is read off the data
   * rather than written down as a constant that would go stale the day the
   * model domain is extended.
   */
  get domain(): Bbox | null {
    const g = this.#geom;
    return g ? { west: g.west, south: g.south, east: g.east, north: g.north } : null;
  }

  #fetchedAt = new Map<number, number>();
  /** Chunk start → when to bother asking again, for spans the model does not
   *  cover. See the empty-window branch in `#fetchWindow`. */
  #empty = new Map<number, number>();
  #inFlight = new Set<number>();
  #chunks = 0;
  #failures = 0;
  #retryAt = 0;
  /** The window route answered with something other than a window — an old
   *  plugin, or the mock. Falls back to one hour at a time, for ever. */
  #noWindow = false;
  #cursor = 0;

  /** Ask for whatever `t` needs, and for the window around it. Safe to call
   *  every tick — TTL-gated, de-duplicated in flight, and capped in
   *  concurrency, so a scrub does not fan out. */
  ensure(t: number): void {
    this.#cursor = t;
    if (Date.now() < this.#retryAt) return;
    if (this.#noWindow) {
      for (const h of [modelHour(t), modelHour(t) + HOUR]) this.#wantHour(h);
      return;
    }
    this.#pump();
  }

  #wantHour(h: number): void {
    if (this.#inFlight.has(h)) return;
    const got = this.#fetchedAt.get(h) ?? 0;
    if (this.#hours.has(h) && Date.now() - got < TTL_MS) return;
    this.#inFlight.add(h);
    void this.#fetchHour(h);
  }

  /**
   * Start whatever chunks the window is still missing, NEAREST THE CURSOR
   * FIRST.
   *
   * ═══ THE WINDOW IS ANCHORED TO THE CLOCK, THE ORDER TO THE CURSOR ═══
   *
   * Two different jobs, and the first version confused them. The SPAN is
   * `now − 12 h .. now + 48 h` because that is the scrubber's own span — the
   * cursor cannot leave it. Anchoring the span to the CURSOR instead made the
   * window slide along with a drag, so scrubbing 20 h forward asked for 20 new
   * hours at the far end: exactly the network the whole change exists to
   * remove. Caught by the proof script's "dragging 30 h out and back triggers
   * NO fetch" — 19 requests — which is a measurement that would not have
   * shown up in any unit test, because nothing there has a cursor that moves.
   *
   * ORDER is the cursor's, though, and that is the other half: a 60 h window
   * fetched left to right leaves the layer blank for several seconds when the
   * cursor is at the right-hand end, which is exactly where a forecast
   * question is asked from. Sorting by distance from the cursor means the
   * first chunk to land is always the one being looked at.
   */
  /** The forward reach the budget allows, given how wet this lattice turned
   *  out to be. See BUDGET_BYTES. */
  #fwdHours(): number {
    const g = this.#geom;
    if (!g || !g.wet) return WANT_FWD_H;
    const perHour = g.wet * 4; // two Int16 arrays
    const affordable = Math.floor(BUDGET_BYTES / perHour) - WANT_BACK_H;
    return Math.max(MIN_FWD_H, Math.min(WANT_FWD_H, affordable));
  }

  #pump(): void {
    if (this.#chunks >= CONCURRENCY) return;
    const base = modelHour(Date.now());
    /* CLAMPED TO THE MODEL'S OWN REACH. Asking past it fails the whole request
     * — ERDDAP does not clamp — and the failure used to take the layer off the
     * map. See `coverage`. Until the first reply tells us the reach, the
     * budgeted span is the best guess; one chunk may overshoot on a cold start
     * and that is handled as a normal empty answer, not an error. */
    const c = this.coverage;
    const first = Math.max(base - WANT_BACK_H * HOUR, c ? c.from : -Infinity);
    const last = Math.min(base + this.#fwdHours() * HOUR, c ? c.to : Infinity);
    if (last < first) return;

    /* Chunk boundaries are aligned to absolute hours, not to the cursor, so a
     * cursor that drifts does not re-cut the window into overlapping pieces
     * and re-fetch hours it already holds. */
    const starts: number[] = [];
    const span = CHUNK_H * HOUR;
    for (let s = Math.floor(first / span) * span; s <= last; s += span) starts.push(s);

    const stale = Date.now() - TTL_MS;
    const now = Date.now();
    const missing = starts.filter((s) => {
      if (this.#inFlight.has(s)) return false;
      if ((this.#empty.get(s) ?? 0) > now) return false;
      for (let h = s; h < s + span; h += HOUR) {
        if (h < first || h > last) continue;
        if (!this.#hours.has(h) || (this.#fetchedAt.get(h) ?? 0) < stale) return true;
      }
      return false;
    });
    if (!missing.length) return;

    missing.sort((a, b) => Math.abs(a + span / 2 - this.#cursor) - Math.abs(b + span / 2 - this.#cursor));
    for (const s of missing.slice(0, CONCURRENCY - this.#chunks)) {
      this.#inFlight.add(s);
      this.#chunks++;
      void this.#fetchWindow(s, Math.min(last, s + span - HOUR));
    }
  }

  async #fetchWindow(from: number, to: number): Promise<void> {
    try {
      const who = provider();
      if (!who) throw new Error('no provider for the model field on this host');
      const path = who === 'marine-data' ? WINDOW_ROUTE : FEED_WINDOW;
      const r = await sources().fetch(
        who,
        `${path}?from=${new Date(from).toISOString()}&to=${new Date(to).toISOString()}`,
        { signal: AbortSignal.timeout(TIMEOUT_MS) }
      );
      if (r.status === 404) {
        /* An older plugin, or the mock, which serves the single-hour route
         * only. Not an error — fall back and say nothing. */
        this.#noWindow = true;
        return;
      }
      if (r.status === 503) throw new Error('the model service is not reachable');
      if (r.status === 502) throw new Error('the provider cannot reach SalishSeaCast');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const w = decodeWindow(await r.arrayBuffer());
      if (w.coverage) this.coverage = w.coverage;

      /* ═══ NO HOURS IS AN ANSWER, NOT A FAILURE ═══
       *
       * The pi returns an empty window when the span is past what the model
       * publishes — which is ordinary, because the scrubber reaches +48 h and
       * the model reaches about +30. Calling it an error is what set `error`,
       * flipped `available()` and had the layer removed from the map. It has
       * told us the coverage, which is the useful part; take that and stop. */
      if (!w.hours.length) {
        /* Remember it was empty. Nothing was stored, so `#pump` would see the
         * chunk as still missing and ask again immediately — a request loop on
         * a span that cannot ever be filled. Normally `coverage` stops the
         * chunk being considered at all from the next pass; this covers the
         * cold start before the first reply, and a pi too old to report
         * coverage. */
        this.#empty.set(from, Date.now() + EMPTY_TTL_MS);
        this.#failures = 0;
        this.#retryAt = 0;
        this.rev++;
        return;
      }
      this.#empty.delete(from);

      this.#adopt(w.geom);
      const now = Date.now();
      for (const h of w.hours) {
        this.#hours.set(h.t, h);
        this.#fetchedAt.set(h.t, now);
      }
      this.source = w.source || this.source;
      this.at = w.at ?? now;
      this.stale = r.headers.get('X-Marine-Stale') === '1';
      this.#ok();
    } catch (e) {
      this.#failed(e);
    } finally {
      this.#inFlight.delete(from);
      this.#chunks--;
      /* Keep filling. Success or failure, the next chunk is decided by what is
       * still missing nearest the cursor — which may have moved while this was
       * in the air. */
      if (!this.error && !this.#noWindow) this.#pump();
      else if (this.#noWindow) this.ensure(this.#cursor);
    }
  }

  /** The single-hour JSON route, for the mock and for a pi that predates the
   *  window. Packed on arrival so there is only ever one sampler. */
  async #fetchHour(hour: number): Promise<void> {
    try {
      const r = await sources().fetch('marine-data', `${ROUTE}?t=${new Date(hour).toISOString()}`, {
        signal: AbortSignal.timeout(TIMEOUT_MS)
      });
      if (r.status === 503) throw new Error('the model service is not reachable');
      if (r.status === 502) throw new Error('the provider cannot reach SalishSeaCast');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = (await r.json()) as SeaCurrentResponse;
      if (!j.lattice || !Array.isArray(j.lattice.u)) throw new Error('malformed field');

      /* Packed against the mask we already hold, where the lattice matches —
       * see packLattice. Two hours packed with two different masks cannot be
       * blended, and a store that dropped the odd one out would re-fetch it
       * and loop. */
      const w = packLattice(hour, j.lattice, this.#geom ?? undefined);
      this.#adopt(w.geom);
      this.#hours.set(hour, w.hours[0]!);
      this.#fetchedAt.set(hour, Date.now());
      this.source = j.source || this.source;
      this.at = j.at ?? Date.now();
      this.stale = r.headers.get('X-Marine-Stale') === '1';
      this.#ok();
    } catch (e) {
      this.#failed(e);
    } finally {
      this.#inFlight.delete(hour);
    }
  }

  /**
   * Take a geometry, and throw the held hours away if it is a different one.
   *
   * The lattice cannot change under a running plugin, but it CAN change across
   * a plugin restart — and every hour's `u`/`v` is indexed through the shared
   * wet mask, so mixing two geometries would not fail, it would draw a field
   * displaced by however much the box moved. Silent and plausible is the worst
   * kind of wrong, so the older hours go.
   *
   * The single-hour path packs each hour's own mask, so its geometries differ
   * hour to hour in `wet` alone; compared on the LATTICE, not on the mask, for
   * that reason — the sampler reads each hour through its own slots only when
   * it is the sole hour, and the fallback path is two hours of the same mask in
   * practice because the mask is the coastline.
   */
  #adopt(g: CurrentGeom): void {
    const cur = this.#geom;
    if (
      cur &&
      (cur.cols !== g.cols ||
        cur.rows !== g.rows ||
        cur.west !== g.west ||
        cur.north !== g.north ||
        cur.wet !== g.wet)
    ) {
      this.#hours.clear();
      this.#fetchedAt.clear();
    }
    this.#geom = g;
  }

  #ok(): void {
    this.error = null;
    this.#failures = 0;
    this.#retryAt = 0;
    this.#prune();
    this.#summarise();
    this.rev++;
  }

  #failed(e: unknown): void {
    this.error = e instanceof Error ? e.message : String(e);
    this.#failures++;
    this.#retryAt = Date.now() + Math.min(RETRY_MAX_MS, RETRY_MIN_MS * 2 ** (this.#failures - 1));
    this.rev++;
  }

  /** Drop hours that have fallen out of the scrubber's span as the clock moves
   *  on. Not the old eviction — that kept TWO hours and was what broke
   *  backward scrubbing. This keeps the whole window and only lets go of hours
   *  outside it by a wide margin, so the memory has a ceiling without the
   *  cursor ever outrunning the data.
   *
   *  Anchored to the CLOCK, like the window itself: pruning around the cursor
   *  would throw away the far end of the window whenever someone scrubbed, and
   *  then fetch it again on the way back. */
  #prune(): void {
    const base = modelHour(Date.now());
    const lo = base - (WANT_BACK_H + CHUNK_H) * HOUR;
    const hi = base + (this.#fwdHours() + CHUNK_H) * HOUR;
    for (const k of this.#hours.keys()) {
      if (k < lo || k > hi) {
        this.#hours.delete(k);
        this.#fetchedAt.delete(k);
      }
    }
  }

  #summarise(): void {
    let lo = Infinity;
    let hi = -Infinity;
    for (const k of this.#hours.keys()) {
      if (k < lo) lo = k;
      if (k > hi) hi = k;
    }
    this.held = this.#hours.size;
    this.from = Number.isFinite(lo) ? lo : null;
    /* The END of the last hour, not its start: an hour labelled 14:00 is the
     * model's field FOR 14:00, and a blend at 14:30 needs 15:00 as well. The
     * covered span the scrubber shades is therefore [first, last], and the
     * sampler is honest about the gap by returning null past `last`. */
    this.to = Number.isFinite(hi) ? hi : null;
  }

  /** The field at `t` as a function of position, or null when nothing held
   *  brackets it. Rebuilt per frame — see `windowSampler` for why that is
   *  cheaper than the grid it replaced. */
  samplerAt(t: number): FieldSampler | null {
    const g = this.#geom;
    if (!g) return null;
    const b = bracket(this.#hours, t);
    if (!b) return null;
    return windowSampler(g, b[0], b[1], t);
  }

  /** True when `t` is inside the model hours actually held. */
  covers(t: number): boolean {
    return bracket(this.#hours, t) !== null;
  }

  /* ═══ THE ROUTER'S TWO VIEWS OF THE SAME WINDOW ═══
   *
   * The particle layer asks this store for one instant and draws a thousand
   * places. The passage router asks the opposite — one place at a time, walking
   * forward through hours — and it does it in a WORKER, so it needs the raw
   * window rather than a closure over it.
   *
   * Both are exposed here rather than by making `#geom`/`#hours` public,
   * because what the router may have is a snapshot to post, not a live handle
   * on a window this store rewrites as the cursor moves. */

  /** The held window, ready to post to the router worker. Null when nothing is
   *  held — which the router reads as "no field", falling back to the gates
   *  alone rather than to a claim of slack water. */
  window(): { geom: CurrentGeom; hours: PackedHour[]; key: string } | null {
    const g = this.#geom;
    if (!g || this.#hours.size === 0) return null;
    const hours = [...this.#hours.values()].sort((a, b) => a.t - b.t);
    /* Identity of the window, so the worker knows when the one it is holding
     * has been replaced rather than merely extended. The span plus the count is
     * enough: a refetch that changes neither has changed nothing the router
     * would notice. */
    const key = `${g.cols}x${g.rows}:${hours[0]!.t}-${hours[hours.length - 1]!.t}:${hours.length}`;
    return { geom: g, hours, key };
  }

  /** A sampler over the same window that takes `t` per call — for deriving a
   *  schedule on this side once the worker has answered. */
  passageSampler(): (lat: number, lon: number, t: number) => { setToDeg: number; kn: number } | null {
    const g = this.#geom;
    if (!g) return () => null;
    return windowCurrentSampler(g, this.#hours);
  }

  /**
   * How strongly the field should draw at `t` — 1 well inside the window,
   * ramping to 0 across the last hour of it.
   *
   * ═══ FADE, DO NOT BLINK ═══
   *
   * The scrubber reaches +48 h and the model publishes about 30 h ahead, so
   * there is a real edge inside the slider's own span and the owner asked for
   * it to be MARKED rather than hidden. A layer that switches off at a
   * boundary reads as a fault — you have to scrub back and forth to work out
   * whether you broke it. A field that thins out as you approach the edge and
   * is gone at it reads as what it is: the model running out.
   *
   * The slider's own track carries the same information ahead of time (see
   * ChartTools), so this is confirmation, not the only warning.
   */
  edgeFade(t: number): number {
    if (this.to === null || this.from === null) return 1;
    const near = Math.min(t - this.from, this.to + HOUR - t);
    if (near <= 0) return 0;
    return Math.min(1, near / HOUR);
  }

  /**
   * The whole field at `t` as a grid.
   *
   * Nothing on the render path uses this any more — the sampler replaced it,
   * which is the point of the change. It stays because the browser proof script
   * reads it to check the sampler against the same instant, and because it is
   * the shape any future consumer that genuinely wants every cell would ask
   * for. Expensive on purpose-built data: 335 646 cells.
   */
  gridAt(t: number): CurrentGrid | null {
    const g = this.#geom;
    if (!g) return null;
    const b = bracket(this.#hours, t);
    if (!b) return null;
    const [a, nxt] = b;
    const w = !nxt || nxt.t === a.t ? 0 : Math.max(0, Math.min(1, (t - a.t) / (nxt.t - a.t)));
    const n = g.cols * g.rows;
    const u: (number | null)[] = new Array(n).fill(null);
    const v: (number | null)[] = new Array(n).fill(null);
    for (let i = 0; i < n; i++) {
      const s = g.lookup[i]!;
      if (s < 0) continue;
      const u0 = a.u[s]! / 100;
      const v0 = a.v[s]! / 100;
      u[i] = nxt ? u0 + (nxt.u[s]! / 100 - u0) * w : u0;
      v[i] = nxt ? v0 + (nxt.v[s]! / 100 - v0) * w : v0;
    }
    const lattice: SeaCurrentLattice = {
      west: g.west,
      south: g.south,
      east: g.east,
      north: g.north,
      step: g.step,
      stepX: g.stepX,
      stepY: g.stepY,
      cols: g.cols,
      rows: g.rows,
      u,
      v
    };
    return gridFor(lattice);
  }

  /* ═══ AVAILABLE MEANS "CAN WORK", NOT "HAS DATA" ═══
   *
   * This first returned `hours.length > 0` and deadlocked the layer stone
   * dead: available needed data, data came from update(), update() only runs
   * while visible, and isVisible() is false when available() is false. The
   * layer could never take its first breath. (The since-removed wind forecast
   * had the same shape of latch, arrived at from the opposite direction.)
   *
   * So it is optimistic — true until a fetch has actually failed, and true
   * again once the backoff expires, which is also where the retry is kicked
   * from, since a layer switched off by its own unavailability cannot ask for
   * one. Having no field YET is not an outage; the row's description says
   * "waiting" and the chart simply has nothing on it for a moment. */
  available(): boolean {
    /* NO PROVIDER AT ALL is a configuration, not an outage — and unlike every
     * other branch here it will not fix itself, so it is the one case where the
     * row should say so rather than wait.
     *
     * ═══ AND IT IS CHECKED BEFORE THE HELD-DATA RULE BELOW, DELIBERATELY ═══
     *
     * This read `provider() === null && this.held === 0`, which looked like the
     * cautious version of the same test and was not: it made a host with no
     * provider go on drawing whatever it happened to be holding, silently, with
     * nothing to refresh it and no reason in the panel. _sources-proof.mjs
     * caught it — every other live layer named the missing provider and this
     * one said nothing at all.
     *
     * The held-data rule exists to survive a FAILED FETCH, and `provider()`
     * cannot report one: it reads `sources().has(…)`, which asks what this host
     * is configured with. A 502 from SalishSeaCast lands in `this.error`, not
     * here, so the incident that rule was written for is untouched by this. The
     * sea-surface store, which took that lesson from this file, never qualified
     * its own null-provider check either. */
    if (provider() === null) return false;
    /* ═══ AND HOLDING DATA BEATS EVERYTHING ═══
     *
     * `available()` false does not grey a row out — it makes `isVisible()`
     * return false, which makes the manager REMOVE THE LAYER FROM THE MAP.
     * That is a fine answer for a layer with nothing to draw and a catastrophic
     * one for a layer holding sixty hours of model, which is what happened when
     * a single edge chunk failed: the whole field disappeared off the chart
     * because one request at the far end of the scrubber went wrong.
     *
     * So while there is anything to draw, this layer is available. A failing
     * fetch is reported through `description()` and the coverage bar; it is not
     * grounds for deleting what we already have. */
    if (this.held > 0) return true;
    if (this.error === null) return true;
    if (Date.now() >= this.#retryAt) {
      // Past the backoff: let it try again on the next tick.
      this.#retryAt = 0;
      return true;
    }
    return false;
  }

  unavailableHint(): string {
    if (provider() === null) return 'no marine-data or layer-cache provider on this host';
    if (this.error) return `${this.error} — the model field needs a provider and a connection`;
    return 'waiting for the model field';
  }

  /** True once there is something to draw — for the row's own wording. */
  hasField(): boolean {
    return this.held > 0;
  }

  /** `t` is the app's time cursor, so the row can say when the cursor has been
   *  parked past what the model reaches. */
  description(t?: number): string {
    if (!this.hasField()) {
      return this.error
        ? `No model field — ${this.error}. Needs the pi and a connection; there is no shipped baseline for this layer yet.`
        : 'Waiting for the model field from the pi…';
    }
    const ageMin = this.at === null ? null : (Date.now() - this.at) / 60_000;
    const span =
      this.from !== null && this.to !== null
        ? ` Holding the forecast from ${heldSpan(this.from, this.to)} local time — moving the time bar inside that window is instant.`
        : '';
    /* BEYOND THE MODEL'S REACH is not an error and must not read as one. The
     * tide and the tidal-stream predictions are shipped data and still answer
     * out here; it is only this layer that has run out. */
    const past =
      t !== undefined && !this.covers(t)
        ? ' BEYOND THE MODEL FORECAST at the parked time — the tide stations and the tidal streams still work there; this field does not.'
        : '';
    return provenance({ source: this.source, ageMin, stale: this.stale }) + span + past;
  }
}

function hhmm(t: number): string {
  const d = new Date(t);
  return `${String(d.getHours()).padStart(2, '0')}:00`;
}

const SHORT_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

function heldSpan(from: number, to: number): string {
  const fromDate = new Date(from);
  const toDate = new Date(to);
  const sameDay =
    fromDate.getFullYear() === toDate.getFullYear() &&
    fromDate.getMonth() === toDate.getMonth() &&
    fromDate.getDate() === toDate.getDate();
  if (sameDay) return `${hhmm(from)} to ${hhmm(to)}`;
  return `${hhmm(from)} ${SHORT_WEEKDAYS[fromDate.getDay()]} to ${hhmm(to)} ${SHORT_WEEKDAYS[toDate.getDay()]}`;
}

export const seaCurrents = new SeaCurrents();
